export type WikiInline =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: WikiInline[] }
  | { type: 'em'; children: WikiInline[] }
  | { type: 'link'; href: string; children: WikiInline[] };

export type WikiBlock =
  | { type: 'heading'; level: 2 | 3; id: string; text: string; children: WikiInline[] }
  | { type: 'paragraph'; children: WikiInline[] }
  | { type: 'list'; ordered: boolean; items: WikiInline[][] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

export function parseWikiInline(source: string): WikiInline[] {
  const out: WikiInline[] = [];
  let text = '';
  const flush = () => {
    if (text) out.push({ type: 'text', text });
    text = '';
  };

  for (let i = 0; i < source.length; ) {
    if (source.startsWith('**', i)) {
      const end = source.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push({ type: 'strong', children: parseWikiInline(source.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if (source[i] === '*' && source[i + 1] !== ' ' && source[i + 1] !== '*') {
      const end = source.indexOf('*', i + 1);
      if (end > i + 1) {
        flush();
        out.push({ type: 'em', children: parseWikiInline(source.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
    }
    if (source[i] === '[') {
      const middle = source.indexOf('](', i);
      const end = middle === -1 ? -1 : source.indexOf(')', middle + 2);
      if (middle > i && end > middle) {
        flush();
        out.push({ type: 'link', href: source.slice(middle + 2, end).trim(), children: parseWikiInline(source.slice(i + 1, middle)) });
        i = end + 1;
        continue;
      }
    }
    text += source[i];
    i += 1;
  }
  flush();
  return out;
}

export const inlineText = (nodes: WikiInline[]): string =>
  nodes.map((node) => (node.type === 'text' ? node.text : inlineText(node.children))).join('');

export function parseWikiMarkdown(source: string): WikiBlock[] {
  const blocks: WikiBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let headingCount = 0;

  const closeParagraph = () => {
    if (paragraph.length > 0) blocks.push({ type: 'paragraph', children: parseWikiInline(paragraph.join(' ')) });
    paragraph = [];
  };
  const closeList = () => {
    if (list) blocks.push({ type: 'list', ordered: list.ordered, items: list.items.map(parseWikiInline) });
    list = null;
  };

  for (const rawLine of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      closeParagraph();
      closeList();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      closeParagraph();
      closeList();
      headingCount += 1;
      const children = parseWikiInline(heading[2].trim());
      blocks.push({
        type: 'heading',
        level: heading[1].length >= 3 ? 3 : 2,
        id: `section-${headingCount}`,
        text: inlineText(children),
        children,
      });
      continue;
    }

    const bullet = BULLET.exec(rawLine);
    const numbered = bullet ? null : NUMBERED.exec(rawLine);
    const item = bullet ?? numbered;
    if (item) {
      closeParagraph();
      const ordered = numbered !== null;
      if (list && list.ordered !== ordered) closeList();
      list ??= { ordered, items: [] };
      list.items.push(item[1].trim());
      continue;
    }

    if (list) {
      list.items[list.items.length - 1] += ` ${line}`;
      continue;
    }
    paragraph.push(line);
  }
  closeParagraph();
  closeList();
  return blocks;
}

export type WikiTocEntry = { id: string; text: string };

export const wikiToc = (blocks: WikiBlock[]): WikiTocEntry[] =>
  blocks.flatMap((block) => (block.type === 'heading' && block.level === 2 ? [{ id: block.id, text: block.text }] : []));

export type WikiLinkTarget = { kind: 'internal'; path: string } | { kind: 'external'; url: string } | { kind: 'none' };

export const wikiLinkTarget = (href: string): WikiLinkTarget => {
  if (href.startsWith('/') && !href.startsWith('//')) return { kind: 'internal', path: href };
  if (href.startsWith('#')) return { kind: 'internal', path: href };
  if (/^https:\/\//i.test(href)) return { kind: 'external', url: href };
  return { kind: 'none' };
};
